import React from 'react';
import { X, Building, MapPin, Phone, Mail, User, Bed, Calendar } from 'lucide-react';

const HospitalDetailsModal = ({ hospital, isOpen, onClose }) => {
  if (!isOpen || !hospital) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Building className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{hospital.name}</h2>
              <p className="text-gray-600">{hospital.code}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Catégorie et Statut */}
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
              {hospital.category}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              hospital.status === 'Opérationnel' ? 'bg-green-100 text-green-800' :
              hospital.status === 'Partiellement opérationnel' ? 'bg-yellow-100 text-yellow-800' :
              hospital.status === 'En maintenance' ? 'bg-blue-100 text-blue-800' :
              'bg-red-100 text-red-800'
            }`}>
              {hospital.status}
            </span>
          </div>

          {/* Localisation */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Localisation</h3>
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                <span>{hospital.city}, {hospital.province}</span>
              </div>
              {hospital.address && (
                <div className="flex items-start text-sm text-gray-600 ml-6">
                  <span>{hospital.address}</span>
                </div>
              )}
              {hospital.healthZone && (
                <div className="flex items-center text-sm text-gray-600 mt-3">
                  <Building className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">Zone de santé: {hospital.healthZone.name} ({hospital.healthZone.code})</span>
                </div>
              )}
            </div>
          </div>

          {/* Contact */}
          {(hospital.phone || hospital.email) && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact</h3>
              <div className="space-y-2">
                {hospital.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="h-4 w-4 text-gray-400 mr-2" />
                    <span>{hospital.phone}</span>
                  </div>
                )}
                {hospital.email && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                    <span>{hospital.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Personnel */}
          {hospital.director && hospital.director.user && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Personnel</h3>
              <div className="flex items-center text-sm text-gray-600">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <span>
                  <strong>Directeur:</strong> Dr. {hospital.director.user.name}
                </span>
              </div>
              {hospital.director.speciality && (
                <div className="text-sm text-gray-600 ml-6 mt-1">
                  Spécialité: {hospital.director.speciality}
                </div>
              )}
            </div>
          )}

          {/* Capacité */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center text-sm text-gray-600">
                <Bed className="h-4 w-4 text-gray-400 mr-2" />
                <span>{hospital.beds || 0} lits disponibles</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <span>Niveau: {hospital.level}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {hospital.description && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-sm text-gray-600">{hospital.description}</p>
            </div>
          )}

          {/* Timestamps */}
          {(hospital.createdAt || hospital.updatedAt) && (
            <div className="flex items-center space-x-4 text-xs text-gray-500 pt-4 border-t border-gray-200">
              {hospital.createdAt && (
                <div className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  <span>Créé le {new Date(hospital.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
              {hospital.updatedAt && (
                <div className="flex items-center">
                  <span>Modifié le {new Date(hospital.updatedAt).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalDetailsModal;
